import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { Button, ContentLoader, List } from 'shared/components';
import { displayValue } from 'shared/helpers';
import { fetchAccountCustomers } from '../../../actions';
import '../../styles.scss';

const PAGE_SIZE = 20;

const CustomersTab = ({ accountId }) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([]);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let isStale = false;
    setLoading(true);
    setError(false);
    fetchAccountCustomers(accountId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : get(payload, 'count', results.length);
        if (!isStale) {
          setCustomers(results);
          setCount(totalCount);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isStale) {
          setError(true);
          setCustomers([]);
          setCount(0);
          setLoading(false);
        }
      });

    return () => {
      isStale = true;
    };
  }, [accountId, currentSort, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const columns = [
    {
      Header: t('Email'),
      accessor: 'email',
      Cell: (row) => displayValue(row.value),
    },
    {
      Header: t('First Name'),
      accessor: 'first_name',
      Cell: (row) => displayValue(row.value),
    },
    {
      Header: t('Last Name'),
      accessor: 'last_name',
      Cell: (row) => displayValue(row.value),
    },
    {
      Header: t('Phone Number'),
      accessor: 'phone',
      Cell: (row) => displayValue(row.value, '-'),
    },
    {
      Header: t('Reference'),
      accessor: 'reference',
      Cell: (row) => displayValue(row.value),
    },
    {
      Header: t('Labels'),
      accessor: 'clabels',
      Cell: (row) => {
        const labels = get(row, 'original.clabels', []);
        if (!labels || labels.length === 0) return displayValue(null, '-');
        const names = labels
          .map((l) => (typeof l === 'string' ? l : get(l, 'name', null)))
          .filter(Boolean)
          .join(', ');
        return displayValue(names, '-');
      },
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading customers')} />;
  }

  if (error) {
    return (
      <div className="CustomerAccount-error">
        <p>{t('Unable to load customers for this account')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!customers.length) {
    return (
      <div className="CustomerAccount-empty">
        {t('No customers for this account')}
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="CustomerAccount-list">
      <List
        data={customers}
        columns={columns}
        page={page}
        pages={pages}
        manual
        minRows={customers.length || 5}
        showPagination={pages > 1}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
        defaultPageSize={PAGE_SIZE}
        loading={loading}
      />
    </div>
  );
};

CustomersTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default CustomersTab;
